import type { LogResponse } from '../../hooks/useDeals';
import { affinityOpportunityUrl } from '../../utils/affinityUrls';
import styles from './DealsPage.module.css';

/** Post-log/push success panel, extracted verbatim from DealsPage.tsx
 *  (D4 componentization, zero behavior change). */

export function PushSuccessPanel({
  actionLabel,
  result,
  onNextPending,
  onBackToPipeline,
  backLabel = 'Back to Pipeline',
}: {
  actionLabel: string;
  result: LogResponse;
  onNextPending: () => void;
  onBackToPipeline: () => void;
  backLabel?: string;
}) {
  const failedFields = Object.entries(result.field_errors || {});
  const warnings = result.partial_warnings || [];
  const isPartial = failedFields.length > 0 || result.status === 'error';
  return (
    <div className={styles.successPanel}>
      <div className={styles.successIcon}>✓</div>
      <div className={styles.successBody}>
        <h4 className={styles.successTitle}>
          {isPartial ? 'Partially pushed' : actionLabel}
        </h4>
        <div className={styles.successGrid}>
          <div className={styles.successLabel}>Fields pushed</div>
          <div>
            {result.fields_pushed.length > 0
              ? result.fields_pushed.join(', ')
              : 'Note only'}
          </div>
          <div className={styles.successLabel}>Note</div>
          <div>
            {result.note_status}
            {result.note_id != null ? ` · ${result.note_id}` : ''}
          </div>
          {failedFields.length > 0 && (
            <>
              <div className={styles.successLabel}>Field errors</div>
              <ul className={styles.successList}>
                {failedFields.map(([field, error]) => (
                  <li key={field}>
                    <strong>{field}</strong>: {error}
                  </li>
                ))}
              </ul>
            </>
          )}
          {warnings.length > 0 && (
            <>
              <div className={styles.successLabel}>Warnings</div>
              <ul className={styles.successList}>
                {warnings.map((warning, i) => (
                  <li key={`${warning}:${i}`}>{warning}</li>
                ))}
              </ul>
            </>
          )}
        </div>
        <div className={styles.successActions}>
          {result.affinity_opp_id != null && (
            <a
              href={affinityOpportunityUrl(result.affinity_opp_id)}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.btnSecondary}
            >
              View Affinity record
            </a>
          )}
          <button type="button" className={styles.btnPrimary} onClick={onNextPending}>
            Next pending update
          </button>
          <button type="button" className={styles.btnSecondary} onClick={onBackToPipeline}>
            {backLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
